import { AccessFlowUpsertAppModelV2, useAppReviewAccessFlowV2 } from '@api';
import { MaterialIcon } from '@components';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import { DocsUrls } from '@utils';
import { useEffect } from 'react';
import { schemaV2 } from '@AccessFlows/organisms/AccessFlowForm/validation';
import { toApproverPolicyUpsertAppModel, toGranteeFilterGroupUpsertAppModel } from '@AccessFlows/common/helpers';
import { useAccessFlowFormContext } from '@AccessFlows/organisms/FlowForm/common/AccessFlowFormContext';

const ERROR_TEXT = 'Consider reducing the resources and permissions in the following ';
const MODAL_TITLE = 'AWS Limits Exceeded';
const MODAL_SUBTITLE =
  'AWS limits its inline policy size, which may result in grant failure. We recommend using flexible Access Scopes and cloud tags to avoid failures.';

function useAccessTargetsReviewErrors() {
  const { formData, setIsReviewing, setHasAccessTargetsReviewErrors } = useAccessFlowFormContext();
  const { reviewAccessFlow, reviewAccessFlowResponse, isAccessFlowReviewLoading } = useAppReviewAccessFlowV2();

  useEffect(() => {
    if (isAccessFlowReviewLoading) return;

    try {
      const validatedValueV2 = schemaV2.validateSync(formData, {
        abortEarly: false,
      });

      const { granteesV2, ...rest } = validatedValueV2;

      const upsertModel: AccessFlowUpsertAppModelV2 = {
        ...rest,
        approver_policy: formData.approver_policy && toApproverPolicyUpsertAppModel(formData.approver_policy),
        grantees: toGranteeFilterGroupUpsertAppModel(granteesV2),
        settings: {},
      };

      setHasAccessTargetsReviewErrors(false);
      reviewAccessFlow(upsertModel);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined;
    }
    // Very important to run in only on formData change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    setIsReviewing(isAccessFlowReviewLoading);
  }, [isAccessFlowReviewLoading, setIsReviewing]);

  useEffect(() => {
    if (!reviewAccessFlowResponse) return;
    setHasAccessTargetsReviewErrors(!reviewAccessFlowResponse.valid);
  }, [reviewAccessFlowResponse, setHasAccessTargetsReviewErrors]);

  return reviewAccessFlowResponse;
}

export function AccessTargetsReviewErrors() {
  const response = useAccessTargetsReviewErrors();

  if (!response || response?.valid) return null;

  return (
    <Alert
      severity="warning"
      variant="outlined"
      action={
        <Button
          component="a"
          href={DocsUrls.AWS_INLINE_POLICIES_LIMITS}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          onClick={() => {}}
          style={{ alignSelf: 'flex-end', margin: '8px' }}
          startIcon={<MaterialIcon symbol="menu_book_outlined" />}
        >
          Read Docs
        </Button>
      }
    >
      <Stack direction="column" spacing={2} width="100%">
        <AlertTitle>{MODAL_TITLE}</AlertTitle>
        <Typography variant="body2">{MODAL_SUBTITLE}</Typography>
        {response.integration_warnings.length > 0 && (
          <>
            <Typography variant="body2">{`${ERROR_TEXT} integrations:`}</Typography>
            <ul>
              {response?.integration_warnings.map((integration, index) => (
                <li key={`integration-${index}`}>{integration.integration.name}</li>
              ))}
            </ul>
          </>
        )}
        {response.bundle_warnings.length > 0 && (
          <>
            <Typography variant="body2">{`${ERROR_TEXT} bundles:`}</Typography>
            <ul>
              {response.bundle_warnings.map((bundle, index) => (
                <li key={`bundle-${index}`}>{bundle.bundle.name}</li>
              ))}
            </ul>
          </>
        )}
      </Stack>
    </Alert>
  );
}
